import { Box, CircularProgress, InputAdornment, TextField, Tooltip } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { LegalEntityParty } from '@types'
import { validateABNorACN as validateABN } from 'utils/validateBusinessIdFormat'
import { useSocketConnection } from 'hooks/useSocketConnection'
import { generateID } from 'utils/generateMessageID'
import { REQUEST_MESSAGE_TYPES, Request } from '../../../schemas/request'
import { useSelector } from 'react-redux'
import { RootState, dispatch } from 'redux/store'
import { PointOfContact } from './forms/types/PointOfContact'
import { FauxTextField } from './EntityRow'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import EditIcon from '@mui/icons-material/Edit'
import { clearAllLegalEntites, startLoading } from 'redux/slices/legalEntities'
import HeadsUpDialog from '../FullDialogBlocker'

const message =
  'The ACN you entered could not be automatically verified. Our team will contact you shortly with an update. Thank you.'

interface BusinessIdAutoCompleteProps {
  optionSelected: LegalEntityParty
  setLabelEntered: (value: string) => void
  setOption: (val: LegalEntityParty) => void
  label: string
  type: string
  initialValues: any
  isSelected: boolean
  clearable: boolean
  resetIssuer?: React.RefObject<any>
  resetTermsRef?: React.RefObject<any>
  handleClear: (type: string) => void
  setShowPointOfContact: (show: boolean) => void
  setPOC?: (value: PointOfContact | null) => void
  setValue: (val: LegalEntityParty) => void
  disabled?: boolean
  isViewOnly?: boolean
  singleEntry?: boolean
  pocFirstNameRef?: React.RefObject<HTMLInputElement>
}

const BusinessIdAutoComplete = ({
  setLabelEntered,
  setOption,
  optionSelected,
  label,
  type,
  setValue,
  setShowPointOfContact,
  setPOC,
  disabled,
  isViewOnly = false,
  singleEntry,
  pocFirstNameRef
}: BusinessIdAutoCompleteProps) => {
  const [abn, setAbn] = useState('')
  const [error, setError] = useState('')
  const [lastSubmitted, setLastSubmitted] = useState('')
  const [previousValidAbn, setPreviousValidAbn] = useState('')
  const [abnValid, setAbnValid] = useState(false)
  const [abnRejected, setAbnRejected] = useState(false)
  const [responseStatus, setResponseStatus] = useState<{
    statusCode?: number
    description?: string
  }>()
  const [matchedEntity, setMatchedEntity] = useState<LegalEntityParty | null>(null)
  const [showACNPopup, setShowACNPopup] = useState(false)

  const legalEntities = useSelector((state: RootState) => state.legalEntities.entities)
  const statusByRequestId = useSelector((state: RootState) => state.legalEntities.statusByRequestId)
  const isLoading = useSelector((state: RootState) => state.legalEntities.isLoading)
  const Status = useSelector((state: RootState) => state.legalEntities.Status)

  const { onSocketSend } = useSocketConnection()
  const requestIdRef = useRef<string | null>(null)
  const abnInputRef = useRef<HTMLInputElement | null>(null)
  const abnTypeRef = useRef<'ABN' | 'ACN'>('ABN')
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (!requestIdRef.current) return
    const status = statusByRequestId?.[requestIdRef.current]
    if (status) setResponseStatus(status)
  }, [statusByRequestId])

  useEffect(() => {
    if (!responseStatus || !lastSubmitted) return

    const match = legalEntities.find((entity) =>
      entity.legalEntityCodes?.some(
        (code) =>
          (code.legalEntityCodeType === 'ABN' || code.legalEntityCodeType === 'ACN') &&
          code.legalEntityCodeData.replace(/\s+/g, '') === lastSubmitted
      )
    )

    if (responseStatus.statusCode === 9023) {
      setShowACNPopup(true)
    } else {
      setShowACNPopup(false)
    }

    if (responseStatus.statusCode === 1023 && match) {
      setMatchedEntity(match)
      setOption(match)
      setValue(match)
      setLabelEntered(match.commonName)
      setPreviousValidAbn(lastSubmitted)
      setError('')
      setAbnValid(true)
      setAbnRejected(false)
      if (match.onboarded) {
        setShowPointOfContact(false)
        setPOC?.(null)
      } else {
        setShowPointOfContact(true)
      }
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    } else if (responseStatus.statusCode !== 1023 && responseStatus.description) {
      setAbn(previousValidAbn)
      setError(responseStatus.description)
      setAbnValid(true)
      setAbnRejected(false)
    }
  }, [responseStatus, lastSubmitted, legalEntities])

  const handleSubmit = () => {
    const cleaned = abn.replace(/\s+/g, '')

    if (!validateABN(cleaned)) {
      setError(`Not a valid ${abnTypeRef.current} code`)
      setAbnValid(false)
      return
    }

    if (cleaned === previousValidAbn || (cleaned === lastSubmitted && isLoading)) return

    abnTypeRef.current = cleaned.length === 9 ? 'ACN' : 'ABN'
    setLastSubmitted(cleaned)
    requestIdRef.current = generateID()
    dispatch(clearAllLegalEntites())
    dispatch(startLoading())

    const request: Request = {
      messageId: requestIdRef.current,
      messageType: REQUEST_MESSAGE_TYPES.QueryLegalEntities,
      QueryLegalEntities: {
        legalEntityCode: {
          legalEntityCodeData: cleaned,
          legalEntityCodeType: abnTypeRef.current
        }
      }
    }

    onSocketSend(request)

    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => {
      setError(Status?.description)
      setAbnValid(false)
      setAbnRejected(true)
    }, 5000)
  }

  useEffect(() => {
    if (!optionSelected?.legalEntityIdentifier) return
    const abnCode = optionSelected.legalEntityCodes?.find(
      (c) => c.legalEntityCodeType === 'ABN' || c.legalEntityCodeType === 'ACN'
    )?.legalEntityCodeData
    //const cleaned = abnCode?.replace(/\s+/g, '') || ''
    if (abnCode) {
      setAbn(abnCode)
      setPreviousValidAbn(abnCode)
      setLastSubmitted(abnCode)
      setAbnValid(true)
      setAbnRejected(false)
      setError('')
    }
  }, [optionSelected?.legalEntityIdentifier])

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [])

  const revertToPreviousValid = () => {
    if (previousValidAbn !== '') {
      setAbn(previousValidAbn)
      setError('')
      setAbnValid(true)
      setAbnRejected(false)
    }
  }
  return (
    <Box>
      {singleEntry || (type === 'issuers' && abn) ? (
        <FauxTextField
          label={`${label} ${abnTypeRef.current}`}
          value={abn}
          showSuccess={!!abn}
          singleEntry
        />
      ) : !showACNPopup ? (
        <TextField
          label={
            type === 'beneficiaries' && !abn
              ? 'Enter beneficiary ABN/ACN to search'
              : `${label} ${abnTypeRef.current} `
          }
          fullWidth
          value={abn}
          autoComplete="off"
          sx={{ width: '98%' }}
          inputRef={abnInputRef}
          onChange={(e) => {
            const value = e.target.value
              .replace(/[^0-9\s]/g, '')
              .replace(/\s{2,}/g, ' ')
              .replace(/^\s+/, '')
            setAbn(value)
            setError('')
            setAbnValid(false)
            setAbnRejected(false)
          }}
          onBlur={revertToPreviousValid}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              handleSubmit()
            }
          }}
          error={!!error}
          helperText={error || (isViewOnly ? 'Press Enter to validate ABN/ACN' : '')}
          disabled={disabled}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {isLoading && type === 'beneficiaries' && abn ? (
                  <CircularProgress size={20} />
                ) : abnValid && !error ? (
                  <>
                    <Tooltip title="Edit ABN/ACN">
                      <Box
                        onClick={() => {
                          setAbnValid(false)
                          setTimeout(() => abnInputRef.current?.focus(), 0)
                        }}
                        sx={{
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          marginLeft: '8px',
                          marginRight: '4px'
                        }}
                      >
                        <EditIcon />
                      </Box>
                    </Tooltip>
                    <Box sx={{ marginLeft: '8px', marginRight: '4px' }}>
                      <Tooltip title="Valid ABN/ACN found">
                        <CheckCircleIcon color="success" />
                      </Tooltip>
                    </Box>
                  </>
                ) : null}
              </InputAdornment>
            )
          }}
        />
      ) : type === 'beneficiaries' && showACNPopup ? (
        <HeadsUpDialog open={showACNPopup} message={message} redirectTo="/dashboard/app" />
      ) : null}
    </Box>
  )
}

export default BusinessIdAutoComplete
